import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Crown, Loader2, Plus, Pencil, Trash2, Search } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../../app/layouts/DashboardLayout';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { ADMIN_NAV } from '../../constants/nav';
import {
  adminFetchVipPrograms,
  adminCreateVipProgram,
  adminUpdateVipProgram,
  adminDeleteVipProgram,
  adminFetchVipApplications,
  adminFetchVipApplication,
  adminReviewVipApplication,
  VipProgramPayload,
  VipReviewPayload,
} from '../../features/vip/api/vip';
import type { VipProgram, VipApplication } from '../../types';
import { StatusBadge } from '../../components/common/StatusBadge';
import { VIP } from '../../constants/testIds';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';

const inputClass =
  'w-full bg-[#050914] border border-white/10 rounded-lg px-3 py-2 text-white placeholder:text-white/30 focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 outline-none transition-colors text-sm';
const labelClass = 'block text-xs uppercase tracking-wider text-white/50 mb-1.5';

const emptyProgram: VipProgramPayload = {
  name: '',
  description: '',
  min_equity: 0,
  min_exist_equity: 0,
  max_inactive_days: 30,
  is_active: true,
  join_instructions: '',
  whatsapp_group_url: '',
  telegram_group_url: '',
  tiktok_url: '',
  discord_group_url: '',
};

const emptyReview: VipReviewPayload = {
  review_result: 'review',
  equity_checked: 0,
  is_activity_valid: false,
  is_exist_equity_valid: false,
  portal_check_note: '',
  violation_note: '',
  admin_note: '',
};

export default function AdminVipPage() {
  const [params] = useSearchParams();
  const userQ = params.get('user') || '';
  const [tab, setTab] = useState<'applications' | 'programs'>('applications');
  const [appSearch, setAppSearch] = useState(userQ);
  const [showRejected, setShowRejected] = useState(false);
  const [programs, setPrograms] = useState<VipProgram[]>([]);
  const [applications, setApplications] = useState<VipApplication[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { if (userQ) { setAppSearch(userQ); setTab('applications'); } }, [userQ]);

  const rejectedCount = useMemo(() => applications.filter((a) => a.status === 'rejected').length, [applications]);
  const filteredApps = useMemo(() => {
    const s = appSearch.trim().toLowerCase();
    let list = showRejected ? applications : applications.filter((a) => a.status !== 'rejected');
    if (s) {
      list = list.filter((a) =>
        (a.member?.email || '').toLowerCase().includes(s) ||
        (a.member?.full_name || '').toLowerCase().includes(s) ||
        (a.broker_name || '').toLowerCase().includes(s)
      );
    }
    return list;
  }, [applications, appSearch, showRejected]);

  const [programDialog, setProgramDialog] = useState(false);
  const [confirmDelProgram, setConfirmDelProgram] = useState<VipProgram | null>(null);
  const [delProgramLoading, setDelProgramLoading] = useState(false);
  const [editingProgram, setEditingProgram] = useState<VipProgram | null>(null);
  const [programForm, setProgramForm] = useState<VipProgramPayload>(emptyProgram);
  const [savingProgram, setSavingProgram] = useState(false);

  const [reviewDialog, setReviewDialog] = useState(false);
  const [activeApp, setActiveApp] = useState<VipApplication | null>(null);
  const [reviewForm, setReviewForm] = useState<VipReviewPayload>(emptyReview);
  const [savingReview, setSavingReview] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([adminFetchVipPrograms(), adminFetchVipApplications()])
      .then(([p, a]) => {
        setPrograms(p);
        setApplications(a);
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  // ----- Programs -----
  const openNewProgram = () => {
    setEditingProgram(null);
    setProgramForm(emptyProgram);
    setProgramDialog(true);
  };
  const activeMembers = (programId: string) =>
    applications.filter((a) => a.vip_program_id === programId && a.status === 'approved').length;

  const openEditProgram = (p: VipProgram) => {
    setEditingProgram(p);    setProgramForm({
      name: p.name,
      description: p.description,
      min_equity: p.min_equity,
      min_exist_equity: p.min_exist_equity,
      max_inactive_days: p.max_inactive_days,
      is_active: p.is_active,
      join_instructions: p.join_instructions || '',
      whatsapp_group_url: p.whatsapp_group_url || '',
      telegram_group_url: p.telegram_group_url || '',
      tiktok_url: p.tiktok_url || '',
      discord_group_url: p.discord_group_url || '',
    });
    setProgramDialog(true);
  };
  const saveProgram = async () => {
    setSavingProgram(true);
    try {
      if (editingProgram) await adminUpdateVipProgram(editingProgram.id, programForm);
      else await adminCreateVipProgram(programForm);
      toast.success('Program tersimpan');
      setProgramDialog(false);
      load();
    } catch (e: any) {
      toast.error('Gagal menyimpan', { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingProgram(false);
    }
  };

  // ----- Review -----
  const openReview = async (a: VipApplication) => {
    setReviewForm({ ...emptyReview, equity_checked: a.submitted_equity });
    setActiveApp(null);
    setReviewDialog(true);
    try {
      const full = await adminFetchVipApplication(a.id);
      setActiveApp(full);
    } catch {
      setActiveApp(a);
    }
  };
  const submitReview = async (result: 'approved' | 'rejected' | 'review') => {
    if (!activeApp) return;
    setSavingReview(true);
    try {
      await adminReviewVipApplication(activeApp.id, { ...reviewForm, review_result: result });
      toast.success(`Review disimpan: ${result}`);
      setReviewDialog(false);
      load();
    } catch (e: any) {
      toast.error('Gagal review', { description: e.response?.data?.detail || e.message });
    } finally {
      setSavingReview(false);
    }
  };

  const tabBtn = (key: 'applications' | 'programs', label: string) => (
    <button
      onClick={() => setTab(key)}
      className={
        tab === key
          ? 'px-4 py-2 rounded-lg text-cyan-400 bg-cyan-400/10 border border-cyan-400/20 text-sm font-medium'
          : 'px-4 py-2 rounded-lg text-white/60 hover:text-white hover:bg-white/5 text-sm transition-colors'
      }
    >
      {label}
    </button>
  );

  return (
    <DashboardLayout navItems={ADMIN_NAV} title="VIP Management">
      <div className="max-w-5xl" data-testid={VIP.adminPage}>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <Crown className="w-5 h-5 text-cyan-400" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-cyan-400">Management</p>
              <h2 className="text-2xl sm:text-3xl tracking-tight font-medium text-white">VIP Management</h2>
            </div>
          </div>
          {tab === 'programs' && (
            <button data-testid={VIP.adminProgramNew} onClick={openNewProgram} className="bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 flex items-center gap-2">
              <Plus className="w-4 h-4" strokeWidth={2} /> Program Baru
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-5">
          {tabBtn('applications', `Pengajuan (${applications.length - (showRejected ? 0 : rejectedCount)})`)}
          {tabBtn('programs', `Program (${programs.length})`)}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" strokeWidth={1.5} />
          </div>
        ) : tab === 'applications' ? (
          <>
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <div className="relative w-full sm:w-96">
                <Search className="w-4 h-4 text-white/40 absolute left-3 top-1/2 -translate-y-1/2" strokeWidth={1.5} />
                <input data-testid="vip-app-search" value={appSearch} onChange={(e) => setAppSearch(e.target.value)} placeholder="Cari nama / email / broker member..." className="w-full bg-[#050914] border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 outline-none focus:border-cyan-400 focus:ring-1 focus:ring-cyan-400/50 transition-colors" />
              </div>
              <label className="inline-flex items-center gap-2 text-xs text-white/60 cursor-pointer select-none" data-testid="vip-toggle-rejected">
                <input type="checkbox" checked={showRejected} onChange={(e) => setShowRejected(e.target.checked)} className="accent-cyan-400 h-4 w-4" />
                Tampilkan yang ditolak {rejectedCount > 0 && <span className="text-white/40">({rejectedCount})</span>}
              </label>
            </div>
            {filteredApps.length === 0 ? (
              <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">{appSearch ? 'Tidak ada pengajuan yang cocok dengan pencarian.' : 'Belum ada pengajuan VIP.'}</p>
            ) : (
              <div className="space-y-2">
                {filteredApps.map((a) => (
                  <div key={a.id} data-testid={VIP.adminAppRow} className={`flex items-center gap-4 bg-white/[0.02] border rounded-xl p-4 transition-colors ${userQ && (a.member?.email || '').toLowerCase() === userQ.toLowerCase() ? 'border-amber-400/40' : 'border-white/10'}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium truncate">{a.member?.full_name || a.member?.email || a.user_id}</p>
                      <p className="text-xs text-white/40 truncate">{a.member?.email} · {a.program?.name || '—'} · {a.broker_name} · ${Number(a.submitted_equity).toLocaleString()}</p>
                    </div>
                    <StatusBadge status={a.status} />
                    <button onClick={() => openReview(a)} className="bg-white/5 text-white border border-white/10 font-medium px-4 py-2 rounded-lg transition-colors hover:bg-white/10 hover:text-cyan-400 text-sm">
                      Review
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : programs.length === 0 ? (
          <p className="text-white/40 text-sm bg-white/[0.02] border border-white/10 rounded-xl p-6">Belum ada program.</p>
        ) : (
          <div className="space-y-2">
            {programs.map((p) => (
              <div key={p.id} className="flex items-center gap-4 bg-white/[0.02] border border-white/10 rounded-xl p-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-medium truncate">{p.name}</p>
                    <StatusBadge status={p.is_active ? 'active' : 'expired'} />
                    {activeMembers(p.id) > 0 && (
                      <span className="text-[10px] uppercase tracking-wider text-emerald-300 bg-emerald-400/10 border border-emerald-400/20 px-2 py-0.5 rounded-full">{activeMembers(p.id)} anggota</span>
                    )}
                  </div>
                  <p className="text-xs text-white/40 truncate">Min equity ${p.min_equity.toLocaleString()} · exist ${p.min_exist_equity.toLocaleString()} · {p.max_inactive_days} hari</p>
                </div>
                <button onClick={() => openEditProgram(p)} className="p-2 rounded-lg text-white/60 hover:text-cyan-400 hover:bg-white/5 transition-colors">
                  <Pencil className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <button data-testid="vip-program-delete-button" onClick={() => setConfirmDelProgram(p)} title="Hapus program" className="p-2 rounded-lg text-white/60 hover:text-red-400 hover:bg-white/5 transition-colors">
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Program Dialog */}
      <Dialog open={programDialog} onOpenChange={setProgramDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProgram ? 'Edit Program VIP' : 'Program VIP Baru'}</DialogTitle>
            <DialogDescription className="text-white/50">Atur syarat equity dan aktivitas program VIP.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className={labelClass}>Nama</label>
              <input className={inputClass} data-testid="vip-program-name" value={programForm.name} onChange={(e) => setProgramForm({ ...programForm, name: e.target.value })} />
            </div>
            <div>
              <label className={labelClass}>Deskripsi</label>
              <textarea className={inputClass} rows={2} value={programForm.description} onChange={(e) => setProgramForm({ ...programForm, description: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className={labelClass}>Min Equity</label>
                <input type="number" className={inputClass} value={programForm.min_equity} onChange={(e) => setProgramForm({ ...programForm, min_equity: Number(e.target.value) })} />
              </div>
              <div>
                <label className={labelClass}>Min Exist</label>
                <input type="number" className={inputClass} value={programForm.min_exist_equity} onChange={(e) => setProgramForm({ ...programForm, min_exist_equity: Number(e.target.value) })} />
              </div>
              <div>
                <label className={labelClass}>Max Inaktif</label>
                <input type="number" className={inputClass} value={programForm.max_inactive_days} onChange={(e) => setProgramForm({ ...programForm, max_inactive_days: Number(e.target.value) })} />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={programForm.is_active} onChange={(e) => setProgramForm({ ...programForm, is_active: e.target.checked })} className="accent-cyan-400 w-4 h-4" />
              Program aktif
            </label>

            <div className="pt-2 mt-2 border-t border-white/10 space-y-3">
              <p className="text-xs uppercase tracking-wider text-cyan-400">Info Grup VIP (ditampilkan ke member VIP aktif)</p>
              <div>
                <label className={labelClass}>Keterangan Cara Gabung Grup</label>
                <textarea className={inputClass} data-testid="vip-join-instructions" rows={3} value={programForm.join_instructions} onChange={(e) => setProgramForm({ ...programForm, join_instructions: e.target.value })} placeholder="Contoh: Setelah disetujui, gabung grup melalui tautan di bawah dan perkenalkan diri Anda." />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Link WhatsApp Grup</label>
                  <input className={inputClass} data-testid="vip-whatsapp-url" value={programForm.whatsapp_group_url} onChange={(e) => setProgramForm({ ...programForm, whatsapp_group_url: e.target.value })} placeholder="https://chat.whatsapp.com/..." />
                </div>
                <div>
                  <label className={labelClass}>Link Telegram Grup</label>
                  <input className={inputClass} data-testid="vip-telegram-url" value={programForm.telegram_group_url} onChange={(e) => setProgramForm({ ...programForm, telegram_group_url: e.target.value })} placeholder="https://t.me/..." />
                </div>
                <div>
                  <label className={labelClass}>Link TikTok</label>
                  <input className={inputClass} data-testid="vip-tiktok-url" value={programForm.tiktok_url} onChange={(e) => setProgramForm({ ...programForm, tiktok_url: e.target.value })} placeholder="https://tiktok.com/@..." />
                </div>
                <div>
                  <label className={labelClass}>Link Discord Grup</label>
                  <input className={inputClass} data-testid="vip-discord-url" value={programForm.discord_group_url} onChange={(e) => setProgramForm({ ...programForm, discord_group_url: e.target.value })} placeholder="https://discord.gg/..." />
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid={VIP.adminProgramSave} onClick={saveProgram} disabled={savingProgram || !programForm.name} className="bg-cyan-400 text-[#050914] font-medium px-6 py-2.5 rounded-lg transition-colors hover:bg-cyan-300 disabled:opacity-50 flex items-center gap-2">
              {savingProgram && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Simpan
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialog} onOpenChange={setReviewDialog}>
        <DialogContent className="bg-[#090D1A] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Review Pengajuan VIP</DialogTitle>
            <DialogDescription className="text-white/50">Verifikasi data member lalu setujui, tolak, atau tandai untuk review.</DialogDescription>
          </DialogHeader>
          {activeApp && (
            <div className="space-y-3">
              <div className="bg-white/[0.03] border border-white/10 rounded-xl p-4 text-sm space-y-1">
                <p className="text-white font-medium">{activeApp.member?.full_name} · {activeApp.member?.email}</p>
                <p className="text-white/50">Program: {activeApp.program?.name || '—'}</p>
                <p className="text-white/50">Broker: {activeApp.broker_name} · Akun: {activeApp.account_number} ({activeApp.account_name})</p>
                <p className="text-white/50">Equity diajukan: ${Number(activeApp.submitted_equity).toLocaleString()} · TV: {activeApp.tradingview_username || '—'}</p>
                {activeApp.member_note && <p className="text-white/50">Catatan member: {activeApp.member_note}</p>}
                {activeApp.files && activeApp.files.length > 0 && (
                  <div className="pt-1">
                    <p className="text-white/40 text-xs">Bukti:</p>
                    {activeApp.files.map((f) => (
                      <a key={f.id} href={f.file_url} target="_blank" rel="noreferrer" className="text-cyan-400 text-xs break-all hover:underline block">{f.file_url}</a>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelClass}>Equity dicek</label>
                  <input type="number" className={inputClass} value={reviewForm.equity_checked} onChange={(e) => setReviewForm({ ...reviewForm, equity_checked: Number(e.target.value) })} />
                </div>
                <div className="flex flex-col justify-end gap-1.5 pb-1">
                  <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                    <input type="checkbox" checked={reviewForm.is_activity_valid} onChange={(e) => setReviewForm({ ...reviewForm, is_activity_valid: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Aktivitas valid
                  </label>
                  <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                    <input type="checkbox" checked={reviewForm.is_exist_equity_valid} onChange={(e) => setReviewForm({ ...reviewForm, is_exist_equity_valid: e.target.checked })} className="accent-cyan-400 w-4 h-4" /> Exist equity valid
                  </label>
                </div>
              </div>
              <div>
                <label className={labelClass}>Portal check note</label>
                <input className={inputClass} value={reviewForm.portal_check_note} onChange={(e) => setReviewForm({ ...reviewForm, portal_check_note: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Catatan pelanggaran</label>
                <input className={inputClass} value={reviewForm.violation_note} onChange={(e) => setReviewForm({ ...reviewForm, violation_note: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>Admin note</label>
                <textarea className={inputClass} rows={2} value={reviewForm.admin_note} onChange={(e) => setReviewForm({ ...reviewForm, admin_note: e.target.value })} />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 flex-wrap">
            <button data-testid={VIP.reviewReview} onClick={() => submitReview('review')} disabled={savingReview} className="bg-amber-400/15 text-amber-300 border border-amber-400/30 font-medium px-4 py-2.5 rounded-lg hover:bg-amber-400/25 transition-colors disabled:opacity-50 text-sm">
              Tandai Review
            </button>
            <button data-testid={VIP.reviewReject} onClick={() => submitReview('rejected')} disabled={savingReview} className="bg-red-400/15 text-red-300 border border-red-400/30 font-medium px-4 py-2.5 rounded-lg hover:bg-red-400/25 transition-colors disabled:opacity-50 text-sm">
              Tolak
            </button>
            <button data-testid={VIP.reviewApprove} onClick={() => submitReview('approved')} disabled={savingReview} className="bg-emerald-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg hover:bg-emerald-300 transition-colors disabled:opacity-50 text-sm flex items-center gap-2">
              {savingReview && <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} />} Setujui
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmDelProgram}
        title="Hapus program VIP?"
        description={
          confirmDelProgram
            ? activeMembers(confirmDelProgram.id) > 0
              ? `⚠️ Program "${confirmDelProgram.name}" masih memiliki ${activeMembers(confirmDelProgram.id)} anggota VIP aktif. Program tidak dapat dihapus sampai anggota dicabut/dipindahkan.`
              : `Program "${confirmDelProgram.name}" akan dihapus permanen. Pengajuan yang sudah ada tidak ikut terhapus.`
            : ''
        }
        confirmLabel={confirmDelProgram && activeMembers(confirmDelProgram.id) > 0 ? 'Tetap Coba Hapus' : 'Hapus'}
        loading={delProgramLoading}
        onConfirm={async () => {
          if (!confirmDelProgram) return;
          setDelProgramLoading(true);
          try { await adminDeleteVipProgram(confirmDelProgram.id); toast.success('Program dihapus'); load(); }
          catch (e: any) { toast.error('Gagal', { description: e.response?.data?.detail || e.message }); }
          finally { setDelProgramLoading(false); setConfirmDelProgram(null); }
        }}
        onCancel={() => setConfirmDelProgram(null)}
      />
    </DashboardLayout>
  );
}
